import React, { useState } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { message, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import Modal from '../../../../components/Modal';
import {
  StyledDescription,
  StyledDragger,
  StyledGuideList,
  StyledHeaderRow,
  StyledTemplateButton,
} from './styles';

/**
 * 엑셀 파싱 결과 — `parseFile` 콜백이 반환해야 하는 contract.
 *
 * 도메인별 파서가 이 형태만 만족하면 본 공유 모달이 그대로 사용 가능. 현재는
 * 정산 기준일 / 완료일 변경 도메인 둘 다 동일한 `parseSettlementIdExcel` 을 사용.
 */
export type ExcelUploadParseResult = {
  ids: number[];
  invalidRows: unknown[];
};

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  /**
   * 파싱된 ID 목록을 상위로 전달. 상위는 그대로 Preview API 호출로 이어가
   * 다른 관리자 페이지의 엑셀 업로드 플로우와 일관성을 유지한다.
   */
  onSubmit: (ids: number[]) => void;
  /**
   * 도메인별 엑셀/CSV 파서. 첫 번째 컬럼 ID 리스트만 추출하는 함수가 표준.
   * 두 정산 변경 도메인은 같은 `parseSettlementIdExcel` 를 공유한다.
   */
  parseFile: (file: File) => Promise<ExcelUploadParseResult>;
  /**
   * 도메인별 템플릿 다운로드 핸들러. 클릭 시 클라이언트가 즉석 xlsx 생성 → 다운로드.
   * 파일명만 도메인별로 다르므로 콜백으로 분리.
   */
  onDownloadTemplate: () => void;
  /**
   * 콘솔 디버깅용 prefix. 무시된 행을 `console.warn` 으로 남길 때 어느 도메인에서
   * 발생했는지 식별하기 위함 (운영팀 콘솔 트러블슈팅용).
   */
  logPrefix?: string;
};

/**
 * 정산 변경 도메인 (기준일 / 완료일) 의 공통 엑셀 업로드 모달.
 *
 * 두 도메인이 모두 사용 — parser 와 template generator 만 props 로 받아 도메인별
 * 차이를 흡수한다. 다음 흐름은 두 도메인 동일:
 *  1) Upload.Dragger 의 beforeUpload 콜백으로 파일을 즉석 파싱 (서버 전송 X)
 *  2) 파싱된 유효 ID + 무시된 행 수 표시
 *  3) "미리보기" 버튼 → 상위 콜백 (`onSubmit`) → 상위에서 즉시 Preview API 호출
 *
 * 무시된 행 정보는 `console.warn` 으로 남김 — 운영팀 콘솔 디버깅용.
 */
export const ExcelUploadModal = ({
  isOpen,
  onCancel,
  onSubmit,
  parseFile,
  onDownloadTemplate,
  logPrefix,
}: Props): React.JSX.Element => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [parsedIdList, setParsedIdList] = useState<number[]>([]);
  const [invalidRowCount, setInvalidRowCount] = useState(0);

  const reset = (): void => {
    setFileList([]);
    setParsedIdList([]);
    setInvalidRowCount(0);
  };

  const handleClose = (): void => {
    reset();
    onCancel();
  };

  const handleOk = (): void => {
    if (parsedIdList.length === 0) {
      message.warning('파일을 업로드해주세요.');
      return;
    }
    onSubmit(parsedIdList);
    reset();
  };

  /**
   * Upload.Dragger 의 beforeUpload 콜백. 파일을 즉석 파싱해 ID 리스트만 추출하고
   * 서버로는 보내지 않는다(false 반환). 파싱 실패/유효 행 0건이면 LIST_IGNORE 로
   * fileList 에 안 남게 처리.
   */
  const handleBeforeUpload = async (
    file: File,
  ): Promise<boolean | typeof Upload.LIST_IGNORE> => {
    try {
      const result = await parseFile(file);
      if (result.ids.length === 0) {
        message.error(
          `파일에서 유효한 ID를 찾지 못했습니다. (무시 ${result.invalidRows.length}행)`,
        );
        return Upload.LIST_IGNORE;
      }
      setParsedIdList(result.ids);
      setInvalidRowCount(result.invalidRows.length);
      setFileList([
        {
          uid: String(Date.now()),
          name: file.name,
          status: 'done',
        },
      ]);
      if (result.invalidRows.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `${logPrefix ?? '[ExcelUploadModal]'} skipped rows:`,
          result.invalidRows,
        );
      }
    } catch (e) {
      message.error(`엑셀 파싱 실패: ${e instanceof Error ? e.message : String(e)}`);
    }
    return false; // 서버 전송은 하지 않음
  };

  return (
    <Modal
      title="엑셀 업로드"
      isOpen={isOpen}
      width={560}
      isFooter
      okText={parsedIdList.length > 0 ? `${parsedIdList.length}건 미리보기` : '미리보기'}
      cancelText="취소"
      onOk={handleOk}
      onClose={handleClose}
      destroyOnClose
    >
      <StyledHeaderRow>
        <span className="guide">정산 ID 리스트를 업로드해주세요.</span>
        {/*
         * 템플릿은 S3 호스팅 없이 클라이언트에서 즉석 xlsx 생성 후 다운로드.
         * (첫 행 `id` 헤더 + 예시 3개)
         */}
        <StyledTemplateButton type="button" onClick={onDownloadTemplate}>
          엑셀 양식 다운로드
        </StyledTemplateButton>
      </StyledHeaderRow>

      <StyledDragger
        multiple={false}
        maxCount={1}
        fileList={fileList}
        accept=".xlsx,.xls,.csv"
        beforeUpload={(file) => handleBeforeUpload(file as File)}
        onRemove={() => {
          reset();
          return true;
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">클릭하거나 파일을 이 영역에 드래그하세요</p>
        <p className="ant-upload-hint">.xlsx, .xls, .csv 파일 지원</p>
      </StyledDragger>

      {parsedIdList.length > 0 && (
        <StyledDescription>
          유효 ID <strong>{parsedIdList.length}</strong>건 파싱됨.
          {invalidRowCount > 0 && (
            <>
              {' '}
              <span className="invalid">무시된 행 {invalidRowCount}건</span> (콘솔에서 상세 확인)
            </>
          )}
        </StyledDescription>
      )}

      <StyledGuideList>
        <li>엑셀(.xlsx, .xls)과 CSV 파일을 지원합니다.</li>
        <li>첫 번째 열에 정산 ID(숫자)를 한 줄에 하나씩 입력해주세요.</li>
        <li>첫 행이 헤더(예: &quot;id&quot;)면 자동으로 무시합니다.</li>
        <li>중복 ID는 한 번만 반영되고, 숫자가 아닌 행은 스킵됩니다.</li>
        <li>미리보기 버튼을 누르면 업로드된 ID로 변경 전/후가 즉시 표시됩니다.</li>
      </StyledGuideList>
    </Modal>
  );
};
